import React from 'react';
import { Tabs, Tab, Box, Button, Typography, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, MenuItem, Dialog, DialogTitle, DialogContent,
  CircularProgress, Card, Tooltip } from '@mui/material';
import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import InventoryTodayStock from './InventoryTodayStock';
import PurchaseOrderRecommendationUI from './PurchaseOrderRecommendationUI';
import {
  formatQuantityForUnit,
  isWeightUnit,
  isWholeOrderQuantityInput,
  normalizeEstimatedStockInput,
} from './orderQuantityRules';

const DEFAULT_SUGGESTION_LOOKBACK_DAYS = 28;
const DEFAULT_SUGGESTION_COVERAGE_DAYS = 14;
const DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT = 20;
const SUGGESTION_BLOCKING_FLAGS = new Set([
  'stock_evidence_missing',
  'stock_baseline_unreliable',
  'usage_evidence_insufficient',
  'unit_basis_unsupported',
  'invalid_multiplier',
  'estimated_stock_unavailable',
  'usage_estimate_unavailable',
]);

const suggestionToday = () => dayjs().format('YYYY-MM-DD');
const planningUnitLabel = unit => (Number(unit) === 1 ? 'kg' : 'pc');
const PLANNING_CELL_BACKGROUND = '#f3f8fc';
const PLANNING_HEADER_BACKGROUND = '#e8f2fa';
const NEW_ORDER_QUANTITY_GUIDE = 'Input qty is the number of supplier order units. Qty, Est. stock and Suggested qty use the unit shown with each product. Suggested qty includes expected use until Promised, followed by 14 days of stock cover.';

const safeSuggestionAlertTolerancePercent = value => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 && numericValue <= 100
    ? numericValue
    : DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT;
};

const suggestionErrorMessage = (error) => {
  const body = error?.responseJSON || error || {};
  const direct = body.detail || body.error || body.message;
  if (direct) {
    return Array.isArray(direct) ? direct.join(' ') : String(direct);
  }
  if (body && typeof body === 'object') {
    const fieldMessages = Object.entries(body).flatMap(([field, messages]) => {
      const values = Array.isArray(messages) ? messages : [messages];
      return values.filter(Boolean).map(message => `${field}: ${String(message)}`);
    });
    if (fieldMessages.length > 0) return fieldMessages.join(' ');
  }
  return error?.message || 'Failed to load order quantity suggestions.';
};

class PurchaseOrderUI extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client ( "none" ),
      tabIndex: 0,
      purchaseOrders: [],
      productEntries: [],
      orderData: {
        type: '',
        promisedDate: '',
        location: '',
        description: '',
        supplier: '',
        PurchaseOrderProductEntry_po: []
      },
      tabs: ['Order History', 'New Order', 'Order Proposal', 'Current Stock'],
      selectedOrder: null,
      showDetailDialog: false,
      internalSupplier: 1,
      suggestionInternalSupplierConfigured: false,
      location: 0,
      locations: [{'iref': 0, 'name': "Select Location"}],
      suggestionLoading: false,
      suggestionLoaded: false,
      suggestionError: '',
      suggestionItems: [],
      suggestionRequest: null,
      suggestionAlertTolerancePercent: DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT,
    };
    this.suggestionRequestId = 0;
    
    this.getData = this.getData.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
    this.fetchPurchaseOrders = this.fetchPurchaseOrders.bind(this);
  }

  componentDidMount() {
    this.fetchPurchaseOrders();
    this.getData ();
  }

  componentWillUnmount() {
    this.suggestionRequestId += 1;
  }

  fetchPurchaseOrders = (locationId = null) => {
    const queryData = {
      "type": 0,
    };
    if (locationId) {
      queryData.location = locationId;
    }
    let data = this.state.client.getData(Config.API_URL_PURCHASE_ORDERS, queryData);
    // console.log(data);
    if ( data ) {
      let checks = [];
      if (Array.isArray(data)) {
        checks = data;
      } else if (Array.isArray(data.purchaseOrder)) {
      checks = data.purchaseOrder;
    } else if (data.purchaseOrder) {
      checks = [data.purchaseOrder];
    }

      this.setState({ purchaseOrders: checks });
    }
  };

  getData () {
    let psiData = this.state.client.getData(Config.API_URL_PRODUCT_SUPPLIER_INFO, {"active": 1});
    if (psiData) {
      this.setState({ psiData: psiData });
    }
    let queryData = {
      // "centralSupply": 1,
      "status": 2,
      "isPurchased": 1,
      "orderBy_name": 1,
      // "supplier": 2
    };
    let data = this.state.client.getData(Config.API_URL_PRODUCT, queryData);

    if (data && psiData) {
      const quantityMultiplierMap = {};
      const psiActiveMap = {};
      const centralSupplyMap = {};
      psiData.productSupplierInfo.forEach(info => {
        if (info.preferred) {  // or your preferred logic
          quantityMultiplierMap[info.product.iref] = info.quantityMultiplier;
          psiActiveMap[info.product.iref] = info.active;
          centralSupplyMap[info.product.iref] = info.centralSupply;
        }
      });
      const products = data.products || {};
      const productEntries = Object.keys(products)
        .map((key, index) => {
          const fData = data.products[key];
          return {
            productId: fData.iref,     // might be duplicated
            productName: fData.name,
            unit: fData.unitType,
            inputQuantity: '',
            quantity: 0,
            estimatedStockOverride: '',
            reactKey: `${fData.iref}-${key}`,
            active: psiActiveMap[fData.iref],
            centralSupply: centralSupplyMap[fData.iref],
            quantityMultiplier: quantityMultiplierMap[fData.iref] ?? 1,  // default 1 if none
          };
        });

      let configData = { "key": "INTERNAL_SUPPLIER_COMPANY_IREF" };
      let supplierConfig = this.state.client.getData(Config.API_URL_CONFIG_SALESAPP, configData);
      let internalSupplier = 1;
      const configuredSupplier = supplierConfig?.SalesAppConfigs?.[0]?.value;
      const suggestionInternalSupplierConfigured = configuredSupplier !== null
        && configuredSupplier !== undefined
        && String(configuredSupplier).trim() !== '';
      if (supplierConfig) {
        internalSupplier = configuredSupplier;
      }
      this.setState({
        products,
        productEntries,
        internalSupplier,
        suggestionInternalSupplierConfigured,
      });
    }
    else {
      alert('Failed to load products.');
    }
  
    let locations = BackofficeReportsApi.getLocations(this.state.client);
    this.setState ( { locations: locations.locations });
  };

  handleLocationFilterChange = (e) => {
    const locationId = e.target.value;
    this.setState({ selectedLocationFilter: locationId }, () => {
      this.fetchPurchaseOrders(locationId);
    });
  };

  selectLocation = (event) => {
    const location = event.target.value;
    this.suggestionRequestId += 1;
    this.setState(prev => ({
      location,
      orderData: {
        ...prev.orderData,
        location,
      },
      suggestionLoading: false,
      suggestionLoaded: false,
      suggestionError: '',
      suggestionItems: [],
      suggestionRequest: null,
      suggestionAlertTolerancePercent: DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT,
      productEntries: prev.productEntries.map(entry => ({
        ...entry,
        estimatedStockOverride: '',
      })),
    }), () => {
      if (location && String(location) !== '0') {
        this.fetchOrderRecommendations();
      }
    });
  }

  handleChange = (event, newValue) => {
    this.setState({ tabIndex: newValue });
  };

  handleOrderChange = (field, value) => {
    this.setState(prev => ({
      orderData: {
        ...prev.orderData,
        [field]: value
      }
    }));
  }

  handleQuantityChange = (index, value) => {
    this.setState(prev => {
      const productEntries = [...prev.productEntries];
      if (!productEntries[index]) {
        return null;
      }
      if (!isWholeOrderQuantityInput(value)) {
        return null;
      }
      const entry = { ...productEntries[index] };
      const normalized = String(value ?? '');
      const inputQty = Number(normalized) || 0;
      entry.inputQuantity = normalized;
      entry.quantity = inputQty * entry.quantityMultiplier;
      productEntries[index] = entry;
      return { productEntries };
    });
  }

  handleEstimatedStockChange = (index, value) => {
    this.setState(prev => {
      const productEntries = [...prev.productEntries];
      if (!productEntries[index]) return null;
      productEntries[index] = {
        ...productEntries[index],
        estimatedStockOverride: value,
      };
      return { productEntries };
    });
  };

  buildSuggestionQuery = () => {
    const {
      orderData,
      internalSupplier,
      suggestionInternalSupplierConfigured,
    } = this.state;

    if (!orderData.location || String(orderData.location) === '0') {
      return { error: 'Select a location to see estimated stock and suggestions.' };
    }
    if (!suggestionInternalSupplierConfigured || !internalSupplier) {
      return { error: 'The internal supplier is not configured.' };
    }

    return {
      query: {
        location: orderData.location,
        supplier: internalSupplier,
        asof: suggestionToday(),
        lookbackDays: DEFAULT_SUGGESTION_LOOKBACK_DAYS,
        coverageDays: DEFAULT_SUGGESTION_COVERAGE_DAYS,
      },
    };
  };

  fetchOrderRecommendations = async () => {
    const validation = this.buildSuggestionQuery();
    if (validation.error) {
      this.setState({
        suggestionLoading: false,
        suggestionLoaded: false,
        suggestionError: validation.error,
        suggestionItems: [],
        suggestionRequest: null,
        suggestionAlertTolerancePercent: DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT,
      });
      return;
    }
    if (!Config.API_URL_PURCHASE_ORDER_RECOMMENDATIONS) {
      this.setState({
        suggestionLoading: false,
        suggestionLoaded: false,
        suggestionError: 'The order-recommendation endpoint is not configured.',
        suggestionItems: [],
        suggestionRequest: null,
        suggestionAlertTolerancePercent: DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT,
      });
      return;
    }

    const requestId = this.suggestionRequestId + 1;
    this.suggestionRequestId = requestId;
    this.setState({
      suggestionLoading: true,
      suggestionLoaded: false,
      suggestionError: '',
      suggestionItems: [],
      suggestionRequest: null,
      suggestionAlertTolerancePercent: DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT,
    });

    try {
      const response = await this.state.client.getDataAsync(
        Config.API_URL_PURCHASE_ORDER_RECOMMENDATIONS,
        validation.query,
      );
      if (requestId !== this.suggestionRequestId) {
        return;
      }
      if (!response || !Array.isArray(response.items)) {
        throw new Error('The recommendation service returned an invalid response.');
      }
      this.setState({
        suggestionLoading: false,
        suggestionLoaded: true,
        suggestionError: '',
        suggestionItems: response.items,
        suggestionRequest: { ...validation.query },
        suggestionAlertTolerancePercent: safeSuggestionAlertTolerancePercent(
          response.meta?.alertTolerancePercent,
        ),
      });
    } catch (error) {
      if (requestId !== this.suggestionRequestId) {
        return;
      }
      this.setState({
        suggestionLoading: false,
        suggestionLoaded: false,
        suggestionError: suggestionErrorMessage(error),
        suggestionItems: [],
        suggestionRequest: null,
        suggestionAlertTolerancePercent: DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT,
      });
    }
  };

  isSuggestionCurrent = () => {
    const {
      orderData,
      internalSupplier,
      suggestionLoaded,
      suggestionRequest,
    } = this.state;

    return Boolean(
      suggestionLoaded
      && suggestionRequest
      && String(suggestionRequest.location) === String(orderData.location)
      && String(suggestionRequest.supplier) === String(internalSupplier)
      && suggestionRequest.asof === suggestionToday()
      && Number(suggestionRequest.lookbackDays) === DEFAULT_SUGGESTION_LOOKBACK_DAYS
      && Number(suggestionRequest.coverageDays) === DEFAULT_SUGGESTION_COVERAGE_DAYS
    );
  };

  getSuggestionItem = (entry) => {
    if (!this.isSuggestionCurrent()) {
      return null;
    }
    return this.state.suggestionItems.find(
      item => String(item.productId) === String(entry.productId),
    ) || null;
  };

  getSafeEstimatedStock = (entry) => {
    const item = this.getSuggestionItem(entry);
    if (
      !item
      || item.estimatedStockReliable !== true
      || item.unitBasisSupported !== true
      || item.estimatedStock === null
      || item.estimatedStock === undefined
      || item.estimatedStock === ''
    ) {
      return null;
    }
    const estimatedStock = Number(item.estimatedStock);
    return Number.isFinite(estimatedStock) ? estimatedStock : null;
  };

  getEditableEstimatedStock = (entry) => {
    const override = String(entry.estimatedStockOverride ?? '').trim();
    if (override !== '') {
      const numeric = Number(override);
      return Number.isFinite(numeric) ? numeric : null;
    }
    return this.getSafeEstimatedStock(entry);
  };

  getPromisedLeadDays = () => {
    const promisedDate = this.state.orderData?.promisedDate;
    if (!promisedDate || !dayjs(promisedDate).isValid()) return null;
    return Math.max(
      0,
      dayjs(promisedDate).startOf('day').diff(dayjs(suggestionToday()).startOf('day'), 'day'),
    );
  };

  getExpectedOutThroughPromisedDate = (item) => {
    const expectedOut = item?.expectedOut === null || item?.expectedOut === undefined
      ? null
      : Number(item.expectedOut);
    const leadDays = this.getPromisedLeadDays();
    if (expectedOut === null || !Number.isFinite(expectedOut) || leadDays === null) return null;
    if (leadDays === 0) return expectedOut;

    const averageDailyUsage = item?.averageDailyUsage === null || item?.averageDailyUsage === undefined
      ? null
      : Number(item.averageDailyUsage);
    if (averageDailyUsage === null || !Number.isFinite(averageDailyUsage) || averageDailyUsage < 0) {
      return null;
    }
    return expectedOut + (averageDailyUsage * leadDays);
  };

  getUsableSuggestion = (entry) => {
    const item = this.getSuggestionItem(entry);
    if (!item) return null;

    const hasBlockingFlag = Array.isArray(item.flags)
      && item.flags.some(flag => SUGGESTION_BLOCKING_FLAGS.has(String(flag)));
    const estimatedStock = this.getEditableEstimatedStock(entry);
    const expectedOut = this.getExpectedOutThroughPromisedDate(item);
    if (
      item.orderFormCompatible !== true
      || estimatedStock === null
      || hasBlockingFlag
      || expectedOut === null
      || !Number.isFinite(expectedOut)
    ) {
      return null;
    }

    const localMultiplier = Number(entry.quantityMultiplier);
    const recommendationMultiplier = Number(item.quantityMultiplier);
    if (
      !Number.isFinite(localMultiplier)
      || localMultiplier <= 0
      || !Number.isFinite(recommendationMultiplier)
      || recommendationMultiplier <= 0
      || Math.abs(recommendationMultiplier - localMultiplier) > 1e-9
    ) {
      return null;
    }

    return Math.ceil(Math.max(0, expectedOut - estimatedStock) / localMultiplier);
  };

  getUsableSuggestedStoredQuantity = (entry) => {
    const suggestedInput = this.getUsableSuggestion(entry);
    const multiplier = Number(entry.quantityMultiplier);
    return suggestedInput === null || !Number.isFinite(multiplier) || multiplier <= 0
      ? null
      : suggestedInput * multiplier;
  };

  getQuantityWarning = (entry) => {
    const inputText = String(entry.inputQuantity ?? '').trim();
    if (inputText === '') return null;

    const enteredQuantity = Number(inputText);
    const suggestedQuantity = this.getUsableSuggestion(entry);
    if (
      !Number.isInteger(enteredQuantity)
      || enteredQuantity < 0
      || suggestedQuantity === null
    ) {
      return null;
    }
    const item = this.getSuggestionItem(entry);
    const estimatedStock = this.getEditableEstimatedStock(entry);
    const expectedOut = this.getExpectedOutThroughPromisedDate(item);
    const multiplier = Number(entry.quantityMultiplier);
    if (estimatedStock === null || expectedOut === null || !Number.isFinite(expectedOut) || !Number.isFinite(multiplier) || multiplier <= 0) {
      return null;
    }
    const recommendedStoredQuantity = suggestedQuantity * multiplier;
    const enteredStoredQuantity = enteredQuantity * multiplier;
    const calculatedNeed = Math.max(0, expectedOut - estimatedStock);
    const tolerancePercent = safeSuggestionAlertTolerancePercent(
      this.state.suggestionAlertTolerancePercent,
    );
    const allowance = Math.max(multiplier, recommendedStoredQuantity)
      * tolerancePercent / 100;
    const shortfall = calculatedNeed - enteredStoredQuantity;
    if (enteredQuantity < suggestedQuantity && shortfall > allowance + 1e-9) {
      return {
        severity: 'error',
        message: `Quantity may be too low. Suggested: ${formatQuantityForUnit(recommendedStoredQuantity, entry.unit)} ${planningUnitLabel(entry.unit)}.`,
        mobile: 'Low',
      };
    }
    const excess = enteredStoredQuantity - calculatedNeed;
    if (enteredQuantity > suggestedQuantity && excess > allowance + 1e-9) {
      return {
        severity: 'warning',
        message: `Quantity may be too high. Suggested: ${formatQuantityForUnit(recommendedStoredQuantity, entry.unit)} ${planningUnitLabel(entry.unit)}.`,
        mobile: 'High',
      };
    }
    return null;
  };

  handleSubmit = () => {
    const { orderData, productEntries, internalSupplier } = this.state;
    // Basic validation for required fields
    if (!orderData.promisedDate || !orderData.location) {
      alert('Please fill in both Date and Location before submitting.');
      return;  // stop submission
    }
    const filteredProducts = productEntries
      .filter(p => p.quantity && !isNaN(p.quantity) && parseFloat(p.quantity) > 0)
      .map(p => ({
        product: p.productId,
        unit: p.unit,
        quantity: parseFloat(p.quantity)
      }));
      
    // Prevent submission if no valid lines
    if (filteredProducts.length === 0) {
      alert("Please enter at least one product line with quantity > 0.");
      return;
    }

    const now = new Date();
    const createdOn = now.toISOString();               // e.g. "2025-06-21T13:45:30.000Z"
    const documentDate = now.toISOString().split('T')[0];  // e.g. "2025-06-21"
    const type = 0;
    const supplier = internalSupplier;

    const payload = {
      ...orderData,
      type,
      supplier,
      createdOn,
      documentDate,
      PurchaseOrderProductEntry_po: filteredProducts
    };
    // console.log(JSON.stringify(payload));
    const result = this.state.client.pushData(Config.API_URL_PURCHASE_ORDERS, payload);
    if (result && result.iref) {
      alert("Order saved!");
      this.suggestionRequestId += 1;
      this.setState(prev => ({
        orderData: { type: 0, promisedDate: '', location: '', supplier: 2, description: '' },
        productEntries: prev.productEntries.map(p => ({ ...p, quantity: '', inputQuantity: '', estimatedStockOverride: '' })),
        location: 0,
        suggestionLoading: false,
        suggestionLoaded: false,
        suggestionError: '',
        suggestionItems: [],
        suggestionRequest: null,
        suggestionAlertTolerancePercent: DEFAULT_SUGGESTION_ALERT_TOLERANCE_PERCENT,
      }));
      this.setState({ tabIndex: 0 });
      this.fetchPurchaseOrders();
    } else {
      console.error('Save failed', result);
      const errorMessage = result?.message || "Save failed.";
      alert(errorMessage);
    }
  };
  
  openOrderDetail = (po) => {
    this.setState({ selectedOrder: po, showDetailDialog: true });
  };

  closeOrderDetail = () => {
    this.setState({ showDetailDialog: false, selectedOrder: null });
  };

  renderOrderList = () => {
    let locations = [{'iref': 0, 'name': "Select Location"}];
    if (this.state.locations){
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];
        let myData = {'iref': fData.iref, 'name': fData.name};
        locations.push ( myData );
      });
    }
    return (
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={this.filterBarSx({ xs: '1fr', sm: '220px auto' })}>
          <TextField
            id="standard-select-location-filter"
            select
            required
            label="Location"
            value={this.state.selectedLocationFilter || 0}
            variant="standard"
            size="small"
            sx={this.filterSx()}
            onChange={this.handleLocationFilterChange}
          >
            {locations.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>{option.name}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <TableContainer sx={{ height: '100%', overflow: 'auto' }}>
            <Table size="small" stickyHeader aria-label="purchase orders table">
              <TableHead>
                <TableRow>
                  <TableCell sx={this.headerCellSx('center')}><b>IREF</b></TableCell>
                  <TableCell sx={this.headerCellSx('left', 105)}><b>Date</b></TableCell>
                  <TableCell sx={this.headerCellSx('left', 125)}><b>Promised</b></TableCell>
                  <TableCell sx={this.headerCellSx('left')}><b>Location</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(this.state.purchaseOrders || []).map((po, idx) => (
                  <TableRow key={idx} hover onClick={() => this.openOrderDetail(po)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={this.bodyCellSx('center')}>{po.iref || po.id}</TableCell>
                    <TableCell sx={this.bodyCellSx('left', false, 105)}>{po.documentDate}</TableCell>
                    <TableCell sx={this.bodyCellSx('left', false, 125)}>{po.promisedDate}</TableCell>
                    <TableCell sx={this.bodyCellSx('left')}>{po.location?.name || po.location?.uid || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  };

  renderNewOrderForm = () => {
    const {
      orderData,
      suggestionLoading,
      suggestionLoaded,
      suggestionError,
      suggestionInternalSupplierConfigured,
    } = this.state;
    const activeEntries = this.state.productEntries
      .map((e, i) => ({ ...e, __idx: i }))           // keep original index
      .filter(p => p.active === true && p.centralSupply === true);
    let locations = [{'iref': 0, 'name': "Select Location"}];
    if (this.state.locations){
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];
        let myData = {'iref': fData.iref, 'name': fData.name};
        locations.push ( myData );
      });
    }
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const minDate = `${yyyy}-${mm}-${dd}`;

    const maxDateObj = new Date(today);
    maxDateObj.setDate(maxDateObj.getDate() + 30);
    const yyyyMax = maxDateObj.getFullYear();
    const mmMax = String(maxDateObj.getMonth() + 1).padStart(2, '0');
    const ddMax = String(maxDateObj.getDate()).padStart(2, '0');
    const maxDate = `${yyyyMax}-${mmMax}-${ddMax}`;
    const hasSelectedLocation = Boolean(
      orderData.location && String(orderData.location) !== '0',
    );
    const suggestionsCurrent = this.isSuggestionCurrent();

    return (
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={this.filterBarSx({ xs: '1fr 1fr', sm: '220px 170px auto' })}>
          <TextField
            id="standard-select-location"
            select
            required
            label="Location"
            value={orderData.location || 0}
            variant="standard"
            size="small"
            sx={this.filterSx()}
            onChange={this.selectLocation}
          >
            {locations.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>{option.name}</MenuItem>
            ))}
          </TextField>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Promised"
              value={orderData.promisedDate ? dayjs(orderData.promisedDate) : null}
              onChange={(newValue) => {
                const formattedDate = newValue ? newValue.format('YYYY-MM-DD') : '';
                this.handleOrderChange('promisedDate', formattedDate);
              }}
              minDate={dayjs(minDate)}
              maxDate={dayjs(maxDate)}
              inputFormat="DD.MM.YYYY"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  size="small"
                  sx={this.filterSx()}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              required
            />
          </LocalizationProvider>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
        </Box>
          <Box
            sx={{
              px: { xs: 1, sm: 1.5 },
              py: 0.5,
              minHeight: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              borderBottom: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Box aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
              {suggestionLoading && (
                <>
                  <CircularProgress size={16} aria-label="Loading stock and suggestions" />
                  <Typography variant="caption">Calculating estimated stock and suggestions…</Typography>
                </>
              )}
              {!suggestionLoading && suggestionError && (
                <Typography color="text.secondary" variant="caption">
                  {suggestionInternalSupplierConfigured
                    ? 'Stock and suggestions could not be loaded. You can still create the order.'
                    : 'Stock suggestions are not configured on this server. You can still create the order.'}
                </Typography>
              )}
              {!suggestionLoading && !suggestionError && suggestionsCurrent && !orderData.promisedDate && (
                <Typography variant="caption" color="text.secondary">
                  Estimated stock updated. Select Promised for suggestions.
                </Typography>
              )}
              {!suggestionLoading && !suggestionError && suggestionsCurrent && !!orderData.promisedDate && (
                <Typography variant="caption" color="text.secondary">
                  Stock and suggestions updated for today and Promised.
                </Typography>
              )}
              {!suggestionLoading && !suggestionError && suggestionLoaded && !suggestionsCurrent && (
                <Typography variant="caption" color="warning.dark">
                  Stock and suggestions need to be refreshed.
                </Typography>
              )}
              {!suggestionLoading && !suggestionError && !suggestionLoaded && !hasSelectedLocation && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  Select location for stock suggestions.
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
              <Tooltip title={NEW_ORDER_QUANTITY_GUIDE} arrow describeChild enterTouchDelay={0} leaveTouchDelay={5000}>
                <Button
                  size="small"
                  variant="text"
                  aria-label="Quantity guide"
                  sx={{ minWidth: { xs: 32, sm: 0 }, px: 0.5, gap: 0.4, textTransform: 'none' }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Quantities</Box>
                </Button>
              </Tooltip>
              {hasSelectedLocation && suggestionInternalSupplierConfigured && !suggestionLoading && (
                <Button
                  size="small"
                  variant="text"
                  onClick={this.fetchOrderRecommendations}
                  sx={{ flexShrink: 0, minWidth: 0, px: 1, textTransform: 'none' }}
                >
                  {suggestionError ? 'Retry' : 'Refresh'}
                </Button>
              )}
            </Box>
          </Box>
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            <Table
              stickyHeader
              size="small"
              aria-label="new purchase order table"
              sx={{ minWidth: { xs: 0, sm: 760 }, width: '100%', tableLayout: 'fixed' }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...this.headerCellSx('left', null, true), width: { xs: '38%', sm: '28%' }, whiteSpace: 'normal' }}><b>Product</b></TableCell>
                  <TableCell sx={{ ...this.headerCellSx('right', null, true), width: { xs: '22%', sm: '16%' }, whiteSpace: 'normal' }}><b>Input qty</b></TableCell>
                  <TableCell sx={{ ...this.headerCellSx('right', null, true), width: { xs: '20%', sm: '15%' }, whiteSpace: 'normal' }}><b>Qty</b></TableCell>
                  <TableCell sx={{ ...this.headerCellSx('right', null, true), display: { xs: 'none', sm: 'table-cell' }, width: '21%', bgcolor: PLANNING_HEADER_BACKGROUND, whiteSpace: 'normal' }}><b>Est. stock</b></TableCell>
                  <TableCell sx={{ ...this.headerCellSx('right', null, true), width: { xs: '20%', sm: '20%' }, bgcolor: PLANNING_HEADER_BACKGROUND, whiteSpace: 'normal' }}><b>Suggested qty</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeEntries.map((entry) => {
                  const estimatedStock = this.getSafeEstimatedStock(entry);
                  const suggestedStoredQuantity = this.getUsableSuggestedStoredQuantity(entry);
                  const quantityWarning = this.getQuantityWarning(entry);
                  const multiplier = Number(entry.quantityMultiplier);
                  const unit = planningUnitLabel(entry.unit);
                  const estimatedStockValue = entry.estimatedStockOverride !== ''
                    ? entry.estimatedStockOverride
                    : formatQuantityForUnit(estimatedStock, entry.unit);
                  return (
                    <TableRow key={entry.reactKey || entry.productId} sx={quantityWarning ? { backgroundColor: quantityWarning.severity === 'error' ? '#ffebee' : '#fff8e1' } : undefined}>
                      <TableCell sx={{ ...this.bodyCellSx('left', false, null, true), width: { xs: '38%', sm: '28%' }, wordBreak: 'break-word' }}>
                        <Box sx={this.planningValueSlotSx('left')}>{entry.productName}</Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {unit}
                          {Number.isFinite(multiplier) && multiplier > 1 && (
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> · 1 input = {formatQuantityForUnit(multiplier, entry.unit)} {unit}</Box>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...this.bodyCellSx('right', false, null, true), width: { xs: '22%', sm: '16%' } }}>
                        <Box sx={this.planningValueSlotSx('right')}>
                          <TextField
                            type="number"
                            size="small"
                            value={entry.inputQuantity}
                            error={quantityWarning?.severity === 'error'}
                            onChange={event => {
                              const value = event.target.value;
                              if (/^\d*$/.test(value)) this.handleQuantityChange(entry.__idx, value);
                            }}
                            inputProps={{ step: 1, inputMode: 'numeric', pattern: '[0-9]*', min: 0, style: { textAlign: 'right' } }}
                            InputProps={{ sx: { py: 0.2, height: 34, width: '100%', fontSize: { xs: '16px', sm: '14px' }, backgroundColor: multiplier > 1 ? '#fff3cd' : 'inherit' } }}
                            sx={{
                              width: '100%',
                              maxWidth: { xs: 64, sm: 90 },
                              minWidth: 0,
                              '& input[type=number]': { MozAppearance: 'textfield' },
                              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                            }}
                          />
                        </Box>
                        {quantityWarning && (
                          <Typography variant="caption" color={quantityWarning.severity === 'error' ? 'error' : 'warning.dark'} sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{quantityWarning.message}</Box>
                            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{quantityWarning.mobile}</Box>
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...this.bodyCellSx('right', false, null, true), width: { xs: '20%', sm: '15%' } }}>
                        <Box sx={this.planningValueSlotSx('right')}>
                          <TextField
                            size="small"
                            value={formatQuantityForUnit(entry.quantity || 0, entry.unit, '0')}
                            inputProps={{ min: 0, style: { textAlign: 'right' } }}
                            InputProps={{ readOnly: true, sx: { py: 0.2, height: 34, width: '100%', fontSize: { xs: '13px', sm: '14px' } } }}
                            sx={{ width: '100%', maxWidth: { xs: 64, sm: 90 }, minWidth: 0 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...this.bodyCellSx('right', false, null, true), display: { xs: 'none', sm: 'table-cell' }, width: '21%', bgcolor: quantityWarning ? 'inherit' : PLANNING_CELL_BACKGROUND }}>
                        <Box sx={this.planningValueSlotSx('right')}>
                        <TextField
                          size="small"
                          value={estimatedStockValue}
                          placeholder="—"
                          onChange={event => {
                            const value = normalizeEstimatedStockInput(event.target.value, entry.unit);
                            if (value !== null) {
                              this.handleEstimatedStockChange(entry.__idx, value);
                            }
                          }}
                          inputProps={{
                            step: isWeightUnit(entry.unit) ? '0.001' : '1',
                            inputMode: isWeightUnit(entry.unit) ? 'decimal' : 'numeric',
                            pattern: isWeightUnit(entry.unit)
                              ? '^-?[0-9]+([.,][0-9]{0,3})?$'
                              : '^-?[0-9]+$',
                            style: { textAlign: 'right' },
                            'aria-label': `Estimated stock for ${entry.productName}`,
                          }}
                          InputProps={{ sx: { height: 34, fontSize: { xs: '16px', sm: '14px' } } }}
                          sx={{ width: '100%', maxWidth: 90, minWidth: 0 }}
                        />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...this.bodyCellSx('right', true, null, true), width: { xs: '20%', sm: '20%' }, bgcolor: quantityWarning ? 'inherit' : PLANNING_CELL_BACKGROUND }}>
                        <Box sx={this.planningValueSlotSx('right')}>
                          {suggestedStoredQuantity === null ? '—' : formatQuantityForUnit(suggestedStoredQuantity, entry.unit)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              px: { xs: 1, sm: 1.5 },
              py: { xs: 1, sm: 1.25 },
              borderTop: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{
                minWidth: { xs: '100%', sm: 220 },
                px: 3,
              }}
              onClick={this.handleSubmit}
            >
              Save Order
            </Button>
          </Box>
      </Box>
    );
  };

  renderOrderDetailDialog = () => {
    const { selectedOrder } = this.state;
    if (!selectedOrder) return null;
    const unitTypeMap = { 0: 'pc', 1: 'kg' };
    return (
      <Dialog open={this.state.showDetailDialog} onClose={this.closeOrderDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: { xs: 1.5, sm: 2 }, pr: 7, fontSize: { xs: '15px', sm: '18px' }, fontWeight: 700 }}>
          Order Detail - {selectedOrder.iref || selectedOrder.id}
          <IconButton
            aria-label="close"
            onClick={this.closeOrderDetail}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1.5, sm: 2 } }}>
          <Typography sx={this.dialogInfoSx()}>Document Date: {selectedOrder.documentDate}</Typography>
          <Typography sx={this.dialogInfoSx()}>Promised Date: {selectedOrder.promisedDate}</Typography>
          <Typography sx={this.dialogInfoSx()}>Location: {selectedOrder.location?.name || '-'}</Typography>
          <Typography sx={this.dialogInfoSx()}>Supplier: {selectedOrder.supplier?.name || '-'}</Typography>

          {selectedOrder.PurchaseOrderProductEntry_po?.length > 0 && (
            <Box mt={2}>
              <TableContainer sx={{ maxHeight: { xs: '65vh', sm: '70vh' }, overflow: 'auto' }}>
              <Table stickyHeader size="small" aria-label="purchase order detail table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={this.headerCellSx('left')}><b>Product</b></TableCell>
                    <TableCell sx={this.headerCellSx('center', 48)}><b>Unit</b></TableCell>
                    <TableCell sx={this.headerCellSx('right', 90)}><b>Quantity</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedOrder.PurchaseOrderProductEntry_po.map((line, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={this.bodyCellSx('left')}>
                        {this.state.productEntries.find(p => String(p.productId) === String(line.product.iref))?.productName || line.product.iref || '-'}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx('center', false, 48)}>
                        {unitTypeMap[this.state.productEntries.find(p => String(p.productId) === String(line.product.iref))?.unit] || '-'}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx('right', false, 90)}>{line.quantity.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  render() {
    const { tabIndex, tabs } = this.state;

    return (
      <Card
        sx={{
          borderRadius: '14px',
          overflow: 'hidden',
          maxWidth: '100%',
          width: '100%',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ background: '#f6f4ef', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Tabs
            value={tabIndex}
            onChange={this.handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 40, sm: 42 },
              px: { xs: 0.25, sm: 0.5 },
              '& .MuiTab-root': {
                minHeight: { xs: 42, sm: 44 },
                minWidth: 'auto',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '13px', sm: '14px' },
                px: { xs: 1.3, sm: 1.8 },
              },
            }}
          >
            {tabs.map((label, idx) => (
              <Tab key={idx} label={label} />
            ))}
          </Tabs>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {tabIndex === 0 && this.renderOrderList()}
          {tabIndex === 1 && this.renderNewOrderForm()}
          {tabIndex === 2 && (
            <PurchaseOrderRecommendationUI
              draft={this.props.proposalDraft}
              onDraftChange={this.props.onProposalDraftChange}
            />
          )}
          {tabIndex === 3 && <InventoryTodayStock />}
        </Box>

        {this.renderOrderDetailDialog()}
      </Card>
    );
  }

  filterBarSx(gridTemplateColumns) {
    return {
      px: { xs: 1, sm: 1.5 },
      py: { xs: 0.75, sm: 1 },
      display: 'grid',
      gridTemplateColumns,
      gap: { xs: 1, sm: 1.25 },
      alignItems: 'end',
      borderBottom: '1px solid rgba(0,0,0,0.08)',
    };
  }

  filterSx() {
    return {
      width: '100%',
      minWidth: 0,
      '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '14px' } },
      '& .MuiInputLabel-root': { fontSize: { xs: '13px', sm: '13px' } },
    };
  }

  dialogInfoSx() {
    return { fontSize: { xs: '13.5px', sm: '14px' }, mb: 0.25 };
  }

  planningValueSlotSx(align = 'right') {
    return {
      minHeight: 34,
      display: 'flex',
      alignItems: 'center',
      justifyContent: align === 'left' ? 'flex-start' : (align === 'center' ? 'center' : 'flex-end'),
      minWidth: 0,
    };
  }

  headerCellSx(align, minWidth = null, compact = false) {
    return {
      padding: compact ? { xs: '8px 3px', sm: '7px 5px' } : { xs: '8px 7px', sm: '7px' },
      textAlign: align,
      whiteSpace: 'nowrap',
      minWidth,
      fontSize: { xs: '13px', sm: '13px', md: '14px' },
      backgroundColor: '#f6f4ef',
    };
  }

  bodyCellSx(align, strong = false, minWidth = null, compact = false) {
    return {
      padding: compact ? { xs: '8px 3px', sm: '7px 5px' } : { xs: '8px 7px', sm: '7px' },
      textAlign: align,
      whiteSpace: align === 'left' ? 'normal' : 'nowrap',
      minWidth,
      fontSize: { xs: '13.5px', sm: '13px', md: '14px' },
      fontWeight: strong ? 600 : 400,
      lineHeight: 1.35,
      verticalAlign: compact ? 'top' : undefined,
    };
  }
}

export default PurchaseOrderUI;
